// Card-image lookup. Despite the filename (kept to avoid churning imports),
// this NO LONGER talks to scryfall.com at runtime — it calls our own API
// (`/api/card`), which returns self-hosted image URLs served from our bucket
// CDN. The browser loads image bytes from our bucket, never Scryfall.
//
// Keyed by exact card name so lookups dedupe across the UI. Hits are cached
// for the tab lifetime; misses (null) get a short TTL so a transient blip
// doesn't permanently break hover for a name.
//
// The `ScryfallCard` type name + `imageFor()` signature are preserved so the
// three consumers (CardHover, ExploreDeck, CardSearch) don't have to change
// their call sites — only the data source moved.

import { getCardMetaBatch, type CardMeta } from "./api";

const NEGATIVE_TTL_MS = 30_000;
interface CacheEntry {
  promise: Promise<ScryfallCard | null>;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();

// Shape preserved for callers. Images now come from our `image_uris` built
// out of self-hosted URLs; `card_faces[1]` carries the back-face image for
// double-faced cards so the flip UI has a second image to show.
export interface ScryfallCard {
  name: string;
  mana_cost?: string;
  type_line?: string;
  oracle_text?: string;
  flavor_text?: string;
  image_uris?: {
    small: string;
    normal: string;
    large: string;
    png: string;
    art_crop: string;
  };
  card_faces?: Array<{
    name: string;
    image_uris?: ScryfallCard["image_uris"];
    mana_cost?: string;
    type_line?: string;
    oracle_text?: string;
  }>;
  scryfall_uri?: string;
  set_name?: string;
  rarity?: string;
  layout?: string;
}

function normalize(name: string): string {
  const front = name.split("//")[0];
  return (front ?? "").trim();
}

// Build the ScryfallCard shape callers expect from our API's flat image URLs.
// Every size key points at the SAME self-hosted `normal` image — we only
// mirror `normal` + `art_crop`, so `large`/`png`/`small` reuse `normal`
// rather than 404 against sizes we don't host. `art_crop` is its own URL.
// A non-null `image_back` becomes `card_faces[1].image_uris` so the flip UI
// (and ExploreDeck's `card_faces` check) sees a real second face.
function toCard(meta: CardMeta): ScryfallCard | null {
  if (!meta.image_normal && !meta.image_back) return null;
  const front = meta.image_normal
    ? {
        small: meta.image_normal,
        normal: meta.image_normal,
        large: meta.image_normal,
        png: meta.image_normal,
        art_crop: meta.image_art_crop ?? meta.image_normal,
      }
    : undefined;

  const card: ScryfallCard = {
    name: meta.name,
    type_line: meta.type_line || undefined,
    mana_cost: meta.mana_cost ?? undefined,
    // set_name not stored separately — surface the uppercased set code so
    // ExploreDeck's "· {set}" line still renders something meaningful.
    set_name: meta.set_code ? meta.set_code.toUpperCase() : undefined,
    image_uris: front,
    // scryfall_uri is a user-facing outbound LINK only (a click), never a
    // runtime fetch — so deriving it from the id keeps zero runtime
    // dependency on scryfall.com while preserving the "View on Scryfall" link.
    scryfall_uri: meta.scryfall_id
      ? `https://scryfall.com/card/${meta.scryfall_id}`
      : undefined,
  };

  if (meta.image_back) {
    const back = {
      small: meta.image_back,
      normal: meta.image_back,
      large: meta.image_back,
      png: meta.image_back,
      art_crop: meta.image_back,
    };
    card.card_faces = [
      { name: meta.name, image_uris: front },
      { name: meta.name, image_uris: back },
    ];
  }
  return card;
}

export function getScryfallCard(rawName: string): Promise<ScryfallCard | null> {
  const trimmed = (rawName ?? "").trim();
  const front = normalize(rawName);
  if (!trimmed) return Promise.resolve(null);
  // Cache by front-face name so DFC halves dedupe with their full-name form.
  const cacheKey = front || trimmed;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) return cached.promise;

  // Our DuckDB stores the FULL card name ("Tony Stark // The Invincible Iron
  // Man") and the batch endpoint matches on exact lower(name). Query BOTH the
  // full name and the front-face form so we hit whichever the DB holds — DFCs
  // need the full name; most cards work with either. Then resolve the result
  // by trying the full name first, front-face second.
  const variants = front && front.toLowerCase() !== trimmed.toLowerCase()
    ? [trimmed, front]
    : [trimmed];
  const promise = getCardMetaBatch(variants)
    .then((map) => {
      const meta =
        map[trimmed.toLowerCase()] ?? (front ? map[front.toLowerCase()] : undefined);
      return meta ? toCard(meta) : null;
    })
    .catch(() => null);

  cache.set(cacheKey, { promise, expiresAt: Date.now() + NEGATIVE_TTL_MS });
  promise.then((card) => {
    if (card) cache.set(cacheKey, { promise, expiresAt: Number.POSITIVE_INFINITY });
  });
  return promise;
}

/** Prefer a single image URL across layout variants (DFC, split, adventure). */
export function imageFor(card: ScryfallCard, size: "small" | "normal" | "large" = "normal"): string | null {
  if (card.image_uris?.[size]) return card.image_uris[size];
  if (card.card_faces && card.card_faces[0]?.image_uris?.[size]) {
    return card.card_faces[0].image_uris[size];
  }
  return null;
}

/** Back-face image for a double-faced card, or null if single-faced. Drives
 *  the click-to-flip viewer. */
export function backImageFor(
  card: ScryfallCard,
  size: "small" | "normal" | "large" = "normal",
): string | null {
  return card.card_faces?.[1]?.image_uris?.[size] ?? null;
}

/**
 * Batched card lookup. One POST to /api/card/batch (DuckDB-backed) hydrates
 * an entire deck's worth of names. Returns a Map keyed by normalized name.
 * Missing cards are simply absent from the map.
 */
export async function getScryfallCollection(
  names: string[],
): Promise<Map<string, ScryfallCard>> {
  const result = new Map<string, ScryfallCard>();
  // Send the FULL trimmed names to the API — DuckDB stores DFCs under their
  // full "Front // Back" name and matches exact lower(name), so a front-face-
  // only query would miss every double-faced card. Dedupe on the full name.
  const unique = Array.from(
    new Map(names.map((n) => [n.trim().toLowerCase(), n.trim()])).values(),
  ).filter(Boolean);
  if (unique.length === 0) return result;

  // /api/card/batch caps at 250 names per request.
  for (let i = 0; i < unique.length; i += 250) {
    const chunk = unique.slice(i, i + 250);
    try {
      const map = await getCardMetaBatch(chunk);
      for (const meta of Object.values(map)) {
        const card = toCard(meta);
        if (!card) continue;
        // Key the result by FRONT-FACE name — ExploreDeck looks cards up by
        // their front face (name.split("//")[0]). Also seed the single-card
        // cache under the same front-face key getScryfallCard uses.
        const key = normalize(card.name).toLowerCase();
        result.set(key, card);
        cache.set(normalize(card.name) || card.name, {
          promise: Promise.resolve(card),
          expiresAt: Number.POSITIVE_INFINITY,
        });
      }
    } catch (err) {
      console.warn("card batch lookup failed:", err);
    }
  }
  return result;
}

/** Heuristic role classification used by ExploreDeck to diversify which
 *  cards get featured when cycling through a 60-card deck 7 at a time. */
export type CardRole =
  | "land"
  | "creature"
  | "instant"
  | "sorcery"
  | "enchantment"
  | "artifact"
  | "planeswalker"
  | "other";

export function roleFor(card: ScryfallCard | undefined): CardRole {
  const type = card?.type_line?.toLowerCase() ?? "";
  if (type.includes("planeswalker")) return "planeswalker";
  if (type.includes("creature")) return "creature";
  if (type.includes("land")) return "land";
  if (type.includes("instant")) return "instant";
  if (type.includes("sorcery")) return "sorcery";
  if (type.includes("enchantment")) return "enchantment";
  if (type.includes("artifact")) return "artifact";
  return "other";
}
